import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import {
  usePregnancies,
  useCreatePregnancy,
  useUpdatePregnancy,
} from '../api/pregnancies-api';
import type { PregnancyOut } from '../types';

interface PregnancySectionProps {
  patientId: string;
}

interface PregnancyFormData {
  edd: string;
  lmp: string;
  edd_method: string;
  fetus_count: number;
  gravida: number;
  term: number;
  preterm: number;
  abortions: number;
  living: number;
  pre_pregnancy_weight: string;
  height_inches: string;
  vbac: boolean;
  problems: string;
}

const EMPTY_FORM: PregnancyFormData = {
  edd: '',
  lmp: '',
  edd_method: '',
  fetus_count: 1,
  gravida: 0,
  term: 0,
  preterm: 0,
  abortions: 0,
  living: 0,
  pre_pregnancy_weight: '',
  height_inches: '',
  vbac: false,
  problems: '',
};

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  delivered: 'secondary',
  loss: 'destructive',
  ectopic: 'destructive',
  terminated: 'outline',
};

function pregnancyToFormData(p: PregnancyOut): PregnancyFormData {
  return {
    edd: p.edd ?? '',
    lmp: p.lmp ?? '',
    edd_method: p.edd_method ?? '',
    fetus_count: p.fetus_count,
    gravida: p.gravida,
    term: p.term,
    preterm: p.preterm,
    abortions: p.abortions,
    living: p.living,
    pre_pregnancy_weight: p.pre_pregnancy_weight != null ? String(p.pre_pregnancy_weight) : '',
    height_inches: p.height_inches != null ? String(p.height_inches) : '',
    vbac: p.vbac,
    problems: p.problems ?? '',
  };
}

function formDataToPayload(f: PregnancyFormData): Record<string, unknown> {
  return {
    edd: f.edd || null,
    lmp: f.lmp || null,
    edd_method: f.edd_method || null,
    fetus_count: f.fetus_count,
    gravida: f.gravida,
    term: f.term,
    preterm: f.preterm,
    abortions: f.abortions,
    living: f.living,
    pre_pregnancy_weight: f.pre_pregnancy_weight ? Number(f.pre_pregnancy_weight) : null,
    height_inches: f.height_inches ? Number(f.height_inches) : null,
    vbac: f.vbac,
    problems: f.problems || null,
  };
}

export function PregnancySection({ patientId }: PregnancySectionProps) {
  const { data: pregnanciesData, isLoading } = usePregnancies(patientId);
  const createMutation = useCreatePregnancy(patientId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [form, setForm] = useState<PregnancyFormData>(EMPTY_FORM);

  const pregnancies = pregnanciesData?.data ?? [];
  const active = pregnancies.find((p) => p.status === 'active');
  const past = pregnancies.filter((p) => p.status !== 'active');

  const updateMutation = useUpdatePregnancy(editingId ?? undefined);

  function handleCreate() {
    setIsCreating(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleEdit(p: PregnancyOut) {
    setEditingId(p.id);
    setIsCreating(false);
    setForm(pregnancyToFormData(p));
  }

  function handleCancel() {
    setIsCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleSave() {
    const payload = formDataToPayload(form);

    if (isCreating) {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Pregnancy created');
          handleCancel();
        },
        onError: () => toast.error('Failed to create pregnancy'),
      });
    } else if (editingId) {
      updateMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Pregnancy updated');
          handleCancel();
        },
        onError: () => toast.error('Failed to update pregnancy'),
      });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const showForm = isCreating || editingId !== null;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Pregnancy</CardTitle>
          {!active && !showForm && (
            <Button size="sm" variant="outline" onClick={handleCreate}>
              <Plus className="mr-1 h-4 w-4" />
              Create Pregnancy
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Active pregnancy summary */}
        {active && !showForm && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={STATUS_VARIANT[active.status] ?? 'outline'}>
                  {active.status}
                </Badge>
                {active.edd && (
                  <span className="text-sm text-muted-foreground">
                    EDD: {format(new Date(active.edd), 'MMM d, yyyy')}
                  </span>
                )}
                {active.gtpal_display && (
                  <span className="text-sm font-mono">{active.gtpal_display}</span>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleEdit(active)}>
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {active.fetus_count > 0 && (
                <span>
                  Fetus count: {active.fetus_count}
                </span>
              )}
              {active.vbac && <Badge variant="outline">VBAC</Badge>}
              {active.problems && (
                <span className="text-red-600">Problems: {active.problems}</span>
              )}
            </div>
          </div>
        )}

        {/* No active pregnancy and not creating */}
        {!active && !showForm && pregnancies.length === 0 && (
          <p className="text-sm text-muted-foreground">No pregnancy records.</p>
        )}

        {/* Inline form for create / edit */}
        {showForm && (
          <PregnancyForm
            form={form}
            setForm={setForm}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={isSaving}
            isEdit={!!editingId}
          />
        )}

        {/* Past pregnancies */}
        {past.length > 0 && (
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPast(!showPast)}
            >
              {showPast ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Past Pregnancies ({past.length})
            </button>
            {showPast && (
              <div className="mt-2 space-y-2">
                {past.map((p) => (
                  <div
                    key={p.id}
                    className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
                  >
                    <Badge variant={STATUS_VARIANT[p.status] ?? 'outline'}>
                      {p.status}
                    </Badge>
                    {p.edd && (
                      <span className="text-muted-foreground">
                        EDD: {format(new Date(p.edd), 'MMM d, yyyy')}
                      </span>
                    )}
                    {p.gtpal_display && (
                      <span className="font-mono">{p.gtpal_display}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Inline Form ───────────────────────────────────────────────── */

interface PregnancyFormProps {
  form: PregnancyFormData;
  setForm: React.Dispatch<React.SetStateAction<PregnancyFormData>>;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  isEdit: boolean;
}

function PregnancyForm({ form, setForm, onSave, onCancel, isSaving, isEdit }: PregnancyFormProps) {
  function update<K extends keyof PregnancyFormData>(key: K, value: PregnancyFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      {/* Dates row */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="preg-edd">EDD</Label>
          <Input
            id="preg-edd"
            type="date"
            value={form.edd}
            onChange={(e) => update('edd', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-lmp">LMP</Label>
          <Input
            id="preg-lmp"
            type="date"
            value={form.lmp}
            onChange={(e) => update('lmp', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-edd-method">EDD Method</Label>
          <Select value={form.edd_method} onValueChange={(v) => update('edd_method', v)}>
            <SelectTrigger id="preg-edd-method" className="w-full">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lmp">LMP</SelectItem>
              <SelectItem value="ultrasound">Ultrasound</SelectItem>
              <SelectItem value="ivf">IVF</SelectItem>
              <SelectItem value="clinical">Clinical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Fetus count */}
      <div className="space-y-1.5">
        <Label htmlFor="preg-fetus-count">Fetus Count</Label>
        <Input
          id="preg-fetus-count"
          type="number"
          min={1}
          max={5}
          className="w-24"
          value={form.fetus_count}
          onChange={(e) => update('fetus_count', Math.max(1, Math.min(5, Number(e.target.value))))}
        />
      </div>

      {/* GTPAL row */}
      <div className="space-y-1.5">
        <Label>GTPAL</Label>
        <div className="flex gap-2">
          {(['gravida', 'term', 'preterm', 'abortions', 'living'] as const).map((field) => {
            const label = { gravida: 'G', term: 'T', preterm: 'P', abortions: 'A', living: 'L' }[field];
            return (
              <div key={field} className="flex flex-col items-center gap-1">
                <span className="text-xs font-medium text-muted-foreground">{label}</span>
                <Input
                  type="number"
                  min={0}
                  className="w-14 text-center"
                  value={form[field]}
                  onChange={(e) => update(field, Math.max(0, Number(e.target.value)))}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Weight / Height row */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="preg-weight">Pre-pregnancy Weight (lbs)</Label>
          <Input
            id="preg-weight"
            type="number"
            min={0}
            value={form.pre_pregnancy_weight}
            onChange={(e) => update('pre_pregnancy_weight', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="preg-height">Height (inches)</Label>
          <Input
            id="preg-height"
            type="number"
            min={0}
            value={form.height_inches}
            onChange={(e) => update('height_inches', e.target.value)}
          />
        </div>
      </div>

      {/* VBAC */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="preg-vbac"
          checked={form.vbac}
          onCheckedChange={(checked) => update('vbac', checked === true)}
        />
        <Label htmlFor="preg-vbac" className="cursor-pointer">
          VBAC
        </Label>
      </div>

      {/* Problems */}
      <div className="space-y-1.5">
        <Label htmlFor="preg-problems">Problems / Notes</Label>
        <Textarea
          id="preg-problems"
          rows={3}
          value={form.problems}
          onChange={(e) => update('problems', e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
}
